import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// Belo Rentals localization — customer-facing site + Host Portal.
// English (default) + Brazilian Portuguese (pt-BR). Admin dashboard stays English.

const en = {
  common: {
    back: "Back",
    cancel: "Cancel",
    save: "Save",
    saveChanges: "Save changes",
    loading: "Loading…",
    remove: "Remove",
    edit: "Edit",
    submit: "Submit",
    days_one: "{{count}} day",
    days_other: "{{count}} days",
    daysShort: "{{count}}d",
    reviews_one: "{{count}} review",
    reviews_other: "{{count}} reviews",
  },
  tripPhotos: {
    needMin: "Please add at least {{min}} photos.",
    saved: "Photos saved.",
    saving: "Saving…",
    submit: "Save photos",
    retention: "Photos are kept for 5 days after the trip ends, then automatically deleted — unless there's an open claim.",
    host_pre: { title: "Pre-trip photos", hint: "Document the car's condition before handoff (min {{min}})." },
    customer_start: { title: "Start your trip", hint: "Add at least {{min}} photos of the car at pickup to start the trip." },
    customer_return: { title: "Return photos", hint: "Photograph the car's condition at return (min {{min}})." },
    host_return: { title: "Return inspection", hint: "Document the car's condition after the trip (min {{min}})." },
    addStart: "Start trip · add photos",
    started: "Trip started",
    addReturn: "Add return photos",
    returnDone: "Return photos added",
    preTrip: "Pre-trip photos",
    preTripDone: "Pre-trip photos added",
    returnInspect: "Return inspection",
    view: "View photos",
    purged: "Photos removed (retention period ended)",
  },
  nav: {
    home: "Home",
    fleet: "Vehicles",
    contact: "Contact",
    hostYourCar: "Host your car",
    hostPortal: "Host Portal",
    signIn: "Sign In",
    bookNow: "Book Now",
    account: "Account",
    myTrips: "My Trips",
    signOut: "Sign out",
    renting: "Renting",
    hosting: "Hosting",
  },
  footer: {
    tagline: "Affordable car rentals in Malden & Clinton, MA. Flexible daily and weekly rates, plus Airport Drop-Off at Logan Airport.",
    airportBadge: "Logan Airport Drop-Off",
    navigate: "Navigate",
    ourFleet: "Vehicles",
    contact: "Contact",
    admin: "Admin",
    reachUs: "Reach Us",
    maldenLocation: "Malden Location",
    clintonLocation: "Clinton Location",
    rights: "All rights reserved.",
  },
  home: {
    heroBadge: "Now offering Logan Airport Drop-Off",
    heroTitle1: "Affordable car rentals,",
    heroTitle2: "done simply.",
    heroSubtitle: "Reliable, fuel-efficient vehicles with flexible daily rates",
    heroSubtitlePrice: "from $50/day",
    heroSubtitleEnd: ". Book online in minutes.",
    browseFleet: "Browse Vehicles",
    statDailyRate: "Daily rate from",
    statBooking: "Online booking",
    statAirport: "Pick up & return",
    statAirportValue: "Logan Airport",
    featureRatesTitle: "Flexible Rates",
    featureRatesDesc: "Rates as low as $35/day when you book a full week.",
    featureBookingTitle: "Fast Booking",
    featureBookingDesc: "Reserve online in under two minutes with instant confirmation.",
    featureReliableTitle: "Reliable & Insured",
    featureReliableDesc: "Well-maintained, fuel-efficient vehicles you can count on.",
    airportService: "Airport Service",
    airportDropOff: "Drop-Off Service",
    airportTitle: "Flying out? We'll handle the drop-off.",
    airportDesc: "Flying into or out of Boston? Logan Airport (BOS) pickup & drop-off is available — the same clean, well-maintained cars, ready when you land.",
    airportPoint1: "Convenient pickup & drop-off at Logan Airport economy parking",
    airportPoint2: "Various economy car rental options",
    airportPoint3: "5 Star Rated on Turo",
    airportCta: "See airport drop-off",
    fleetLabel: "Our Vehicles",
    fleetTitle: "The Lineup",
    viewAll: "View All",
    ctaTitle: "Ready when you are.",
    ctaDesc: "Pick your car & choose your dates.",
    ctaButton: "Reserve Now",
  },
  fleet: {
    checkAvailability: "Check availability for your dates",
    pickupDate: "Pickup date",
    pickupTime: "Pickup time",
    returnDate: "Return date",
    returnTime: "Return time",
    pickup: "Pickup",
    return: "Return",
    search: "Search",
    clear: "Clear",
    showing: "Showing vehicles available",
    loading: "Loading fleet…",
    emptyFiltered: "No vehicles available for those dates. Try a different window.",
    emptyCategory: "No vehicles in this category yet.",
    all: "All",
    sortBy: "Sort by",
    sortPriceAsc: "Price: Low to High",
    sortPriceDesc: "Price: High to Low",
    sortNearest: "Nearest to me",
    geoLocating: "Finding vehicles closest to you…",
    geoOk: "Sorted by distance from your location.",
    geoDenied: "Location unavailable — showing all vehicles. Enable location access to sort by nearest.",
    vehicle: "vehicle",
    vehicles: "vehicles",
  },
  inspection: {
    needAll: "Please add all required photos before submitting.",
    saved: "Inspection saved — every photo is timestamped and stored with your trip.",
    photosRequired: "photos required",
    submit: "Submit inspection",
    protect: "These timestamped photos protect you and the host. They're stored securely with the reservation and reviewed if there's ever a dispute.",
    customer_start: {
      title: "Vehicle check-in",
      hint: "Take all 9 photos before driving off — your trip can't begin without them.",
    },
    customer_return: {
      title: "Vehicle check-out",
      hint: "Take the same 9 photos at return to release your deposit quickly.",
    },
    angles: {
      front: "Front of vehicle",
      rear: "Rear of vehicle",
      driver_side: "Driver side",
      passenger_side: "Passenger side",
      wheels: "All wheels",
      interior: "Interior",
      dashboard: "Dashboard",
      odometer: "Odometer",
      fuel_level: "Fuel level",
    },
  },
  veriff: {
    title: "Identity verification",
    intro: "For everyone's safety, we verify each renter once with a driver's license and a quick selfie. It takes about 2 minutes.",
    profileIntro: "Verify once with your driver's license and a quick selfie — then book any vehicle instantly, every time.",
    start: "Start verification",
    starting: "Preparing your secure verification…",
    checking: "Checking your verification result…",
    approved: "You're verified! Continuing…",
    underReview: "Your verification was submitted and is under review. We'll email you as soon as it's approved — your reservation is saved.",
    declined: "We couldn't verify your identity. Please try again or call us at 978-221-1451.",
    error: "Verification could not be started. Please try again.",
    tryAgain: "Try again",
    verifiedBadge: "Identity Verified",
    notVerified: "Not verified yet",
    verifyNow: "Verify now",
    stepNote: "Quick one-time identity check before payment (~2 min)",
    signInFirst: "Please sign in or create an account to book — every renter is identity-verified once.",
    createAccountToContinue: "One quick step — create a free account to lock in your booking.",
    reverifyNeeded: "We need you to re-verify your identity before your next booking.",
    addDriver: "Add an additional driver",
    additionalDriverNote: "The additional driver will receive an email to verify their identity before the trip starts.",
    driverName: "Driver full name",
    driverEmail: "Driver email",
    driverIncomplete: "Please enter the additional driver's name and email.",
    driverPageTitle: "Driver verification",
    driverPageIntro: "{{customer}} added you as an additional driver for this Belo Rentals trip. Verify your identity to be approved to drive.",
    driverLabel: "Additional driver",
    driverVerified: "You're verified! You are approved as an additional driver for this trip.",
    legalName: "Legal name",
    dob: "Date of birth",
    licenseNumber: "License number",
    issuedBy: "Issued by",
    expires: "Expires",
    verifiedOn: "Verified on",
  },
  carCard: {
    perDay: "/day",
    beforeTaxes: "before taxes",
    fromWeek: "from · ${{rate}}/week",
    refundableDeposit: "+ ${{deposit}} refundable deposit",
    stock: "Stock",
    details: "Details",
    book: "Book",
    unavailable: "Unavailable",
    removeSaved: "Remove from saved",
    saveForLater: "Save for later",
  },
  carDetail: {
    notFound: "This vehicle could not be found.",
    backToFleet: "Back to Fleet",
    confirmingPayment: "Confirming your payment…",
    dontClose: "Please don't close this window.",
    perDayFrom: "/ day — from",
    refundableDeposit: "refundable deposit",
    viewPhotos: "View Photos",
    rentalRates: "Rental Rates",
    fromPerDay: "from",
    week: "1 week",
    sevenDays: "(7 days)",
    extraDay: "Each extra day after a week",
    mileageLabel: "Mileage:",
    mileageDetail: "{{perDay}} mi/day · {{perWeek}} mi/week · {{perMonth}} mi/month included. ${{overage}}/mi over, or add unlimited for ${{unlimited}}/day.",
    depositDetail: "${{deposit}} refundable deposit — returned after a clean, full-tank, damage-free, on-time return.",
    bestValue: "Best value",
    ratesDisclaimer: "Rates shown are starting prices and may vary based on vehicle, availability, rental dates, pickup location, seasonal demand, and other factors. Taxes, service fees, deposits, optional protection products, and additional charges are not included in the displayed rental rates.",
    browseVehicles: "Browse Available Vehicles",
    vehicleDetails: "Vehicle details",
    seats: "Seats",
    transmission: "Transmission",
    fuel: "Fuel",
    mileage: "Mileage",
    category: "Category",
    miUnit: "mi",
    stockHash: "Stock #",
    vin: "VIN",
    aboutCar: "About this car",
    features: "Features",
    airportNote: "Logan Airport drop-off available in the booking panel",
    days: "days",
  },
  booking: {
    dueNow: "due now",
    perDayFrom: "/ day — from",
    inclDeposit: "incl. ${{deposit}} refundable deposit",
    pickup: "Pickup",
    return: "Return",
    pickupDate: "Pickup date",
    returnDate: "Return date",
    dob: "Date of Birth",
    youngDriverNote: "Under 25 — a ${{fee}} young driver fee applies.",
    pickupLocation: "Pickup Location",
    pickupTbd: "Provided after booking",
    milesFromLogan: "7 miles from Logan Airport",
    cancellationPolicy: "Cancellation Policy",
    refundable: "Refundable",
    refundableDesc: "Cancel for a full rental refund.",
    nonRefundable: "Non-ref.",
    off10: "10% OFF",
    nonRefDesc: "Save 10%, no refund.",
    nonRefIneligible: "Book {{days}}+ days ahead.",
    airportOption: "Pick up & Drop off at Logan Airport",
    airportOptionDesc: "Flat fee to pick up & drop off at Boston Logan.",
    unlimitedMiles: "Unlimited miles",
    unlimitedMilesDesc: "Skip the {{miles}}-mi/day limit — drive as far as you like.",
    perDay: "/day",
    discountCode: "Discount Code",
    enterCode: "Enter code",
    apply: "Apply",
    yourDetails: "Your Details",
    fullName: "Full Name",
    email: "Email",
    phone: "Phone",
    notesOptional: "Notes (optional)",
    notesPlaceholder: "Any special requests…",
    unavailableWarning: "This vehicle is currently unavailable for booking.",
    overlapWarning: "Your dates overlap with unavailable dates. Please pick another range.",
    summaryPlaceholder: "Select your dates and date of birth to see the full price.",
    duration: "Duration",
    milesIncluded: "Miles included",
    unlimited: "Unlimited",
    rental: "Rental",
    rentalBreakdown: "Rental breakdown",
    subtotal: "Subtotal ({{count}} days)",
    subtotal_one: "Subtotal ({{count}} day)",
    nonRefDiscount: "Non-refundable (−10%)",
    serviceFee: "Service fee ({{pct}}%)",
    tax: "Tax",
    youngDriverFee: "Young driver fee",
    airportDropoff: "Airport drop-off",
    discount: "Discount ({{code}})",
    tripCost: "Trip cost",
    tripCostNote: "(rental + tax)",
    refundableDepositRow: "Refundable deposit",
    depositNote: "Held during your trip — returned to you afterward.",
    payNow: "Pay now",
    redirecting: "Redirecting to secure checkout…",
    reservePay: "Reserve & Pay {{amount}}",
    reservePayShort: "Reserve & Pay",
    reserve: "Reserve",
    securePayment: "Secure payment via Stripe. ${{deposit}} deposit fully refundable.",
    completeDetails: "Complete your trip details to reserve.",
    completeRental: "Please complete the rental details.",
  },
  confirm: {
    title: "Reservation Confirmed",
    thankYou: "Thank you, {{name}}. Your payment of {{amount}} was received and your reservation is confirmed. We'll contact you at {{phone}} with pickup details.",
    vehicle: "Vehicle",
    pickupLocation: "Pickup location",
    pickup: "Pickup",
    return: "Return",
    duration: "Duration",
    mileage: "Mileage",
    unlimited: "Unlimited",
    milesIncluded: "{{miles}} mi included",
    rental: "Rental",
    rentalNonRef: "Rental (non-refundable −10%)",
    serviceFee: "Service fee ({{pct}}%)",
    tax: "Tax",
    youngDriverFee: "Young driver fee",
    airportRow: "Pick up & Drop off at Logan Airport",
    unlimitedMiles: "Unlimited miles",
    discount: "Discount ({{code}})",
    refundableDeposit: "Refundable deposit",
    paid: "Paid",
    totalPaid: "Total Paid",
    agreementSigned: "Rental agreement signed by {{name}} — a copy has been emailed to you.",
    browseMore: "Browse More",
    home: "Home",
  },
  agreement: {
    title: "Rental Agreement",
    review: "Review the terms and sign to continue to secure payment.",
    vehicle: "Vehicle",
    renter: "Renter",
    tripStart: "Trip start",
    tripEnd: "Trip end",
    pickupReturn: "Pickup & return",
    mileage: "Mileage",
    unlimited: "Unlimited",
    milesIncluded: "{{miles}} mi included",
    refundableDeposit: "Refundable deposit",
    totalDueNow: "Total due now",
    prohibited: "Strictly prohibited uses",
    loadingAgreement: "Loading agreement…",
    signLabel: "Type your full legal name to sign",
    signPlaceholder: "Full legal name",
    agreeText: "I, <0>{{name}}</0>, have read and agree to the Belo Rentals Rental Agreement. I understand the deposit is refundable only if all return conditions are met, and that rideshare/for-hire use is prohibited. Electronically signed {{date}}.",
    cancel: "Cancel",
    redirecting: "Redirecting…",
    signPay: "Sign & Pay {{amount}}",
  },
  contact: {
    label: "Get in touch",
    title: "Let's talk",
    subtitle: "Questions about a rental or our Logan Airport drop-off service? Reach out — we're happy to help.",
    phone: "Phone",
    email: "Email",
    maldenLocation: "Malden Location",
    clintonLocation: "Clinton Location",
    airportService: "Airport Service",
    airportAvailable: "Logan Airport Drop-Off available",
    name: "Name",
    namePlaceholder: "John Doe",
    phonePlaceholder: "(978) 000-0000",
    emailPlaceholder: "you@email.com",
    message: "Message",
    messagePlaceholder: "How can we help?",
    sending: "Sending…",
    sendMessage: "Send Message",
    sentToast: "Message sent! We'll be in touch soon.",
  },
  auth: {
    welcomeBack: "Welcome back",
    signInSub: "Sign in to manage your reservations.",
    email: "Email",
    password: "Password",
    forgotPassword: "Forgot password?",
    signingIn: "Signing in…",
    signIn: "Sign In",
    newHere: "New to Belo Rentals?",
    createAccount: "Create an account",
    loginFailed: "Login failed",
    createTitle: "Create your account",
    createSub: "Track your trips and book faster next time.",
    checkoutHandoffSub: "One last step — set a password to finish your booking. We've filled in the rest.",
    fullName: "Full Name",
    phone: "Phone",
    phoneInvalid: "Please enter a valid US phone number.",
    passwordMin: "At least 8 characters",
    creating: "Creating account…",
    createButton: "Create Account",
    haveAccount: "Already have an account?",
    signInLink: "Sign in",
    registerFailed: "Registration failed",
    resetTitle: "Reset password",
    resetSub: "Enter your email and we'll send you a reset link.",
    sending: "Sending…",
    sendResetLink: "Send Reset Link",
    backToSignIn: "Back to sign in",
    checkEmail: "Check your email",
    checkEmailSub: "If an account exists for <0>{{email}}</0>, we've sent a link to reset your password. The link expires in 1 hour.",
    invalidLink: "Invalid link",
    invalidLinkSub: "This reset link is missing or invalid.",
    requestNew: "Request a new link",
    passwordUpdated: "Password updated",
    redirectingSignIn: "Redirecting you to sign in…",
    setNewPassword: "Set a new password",
    setNewSub: "Choose a strong password you'll remember.",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    reenterPassword: "Re-enter password",
    updating: "Updating…",
    updatePassword: "Update Password",
    passwordsNoMatch: "Passwords do not match.",
    passwordUpdatedToast: "Password updated! Please sign in.",
  },
  account: {
    dashboard: "Dashboard",
    trips: "My Trips",
    messages: "Messages",
    saved: "Saved",
    receipts: "Receipts",
    profile: "Profile",
    settings: "Settings",
    signOut: "Sign out",
  },
  dashboard: {
    tripInProgress: "Trip in progress",
    nextTrip: "Your next trip · in {{days}} days",
    noUpcoming: "No upcoming trips",
    noUpcomingSub: "Browse the fleet and book your next ride.",
    browseFleet: "Browse fleet",
    viewTrip: "View trip details",
    upcomingTrips: "Upcoming trips",
    activeNow: "Active now",
    completed: "Completed",
    totalSpent: "Total spent",
    reviewNudge_one: "You have {{count}} trip waiting for a review.",
    reviewNudge_other: "You have {{count}} trips waiting for a review.",
    reviewCar: "Review {{car}}",
  },
  trips: {
    upcoming: "upcoming",
    active: "active",
    completed: "completed",
    cancelled: "cancelled",
    searchPlaceholder: "Search by car…",
    empty: "No {{tab}} trips.",
    paid: "Paid",
    unpaid: "Unpaid",
    details: "Details",
    messageOwner: "Message owner",
    leaveReview: "Leave a review",
    rental: "Rental",
    tax: "Tax",
    serviceFee: "Service fee ({{pct}}%)",
    unlimitedMiles: "Unlimited miles",
    youngDriverFee: "Young driver fee",
    airportDropoff: "Airport drop-off",
    discount: "Discount ({{code}})",
    refundableDeposit: "Refundable deposit",
    totalPaid: "Total paid",
    mileage: "Mileage",
    unlimited: "Unlimited",
    agreement: "Agreement",
    signedBy: "Signed by {{name}}",
  },
  receipts: {
    empty: "No receipts yet",
    emptySub: "Paid bookings will show a downloadable receipt here.",
    vehicle: "Vehicle",
    dates: "Dates",
    total: "Total",
    receipt: "Receipt",
    downloadError: "Could not download receipt.",
  },
  saved: {
    empty: "No saved cars yet",
    emptySub: "Tap the heart on any car to save it for later.",
    browseFleet: "Browse the fleet",
    perDay: "/day",
    book: "Book",
  },
  customerSettings: {
    account: "Account",
    paymentMethods: "Payment methods",
    addCard: "Add card",
    redirecting: "Redirecting…",
    cardsNote: "Cards are stored securely by Stripe — Belo Rentals never sees your card number.",
    noCards: "No saved cards yet. Add one to check out faster next time.",
    cardSaved: "Card saved! It will be available at your next checkout.",
    cardRemoved: "Card removed.",
    defaultUpdated: "Default card updated.",
    default: "Default",
    expires: "Expires",
    makeDefault: "Make default",
    remove: "Remove",
    notifications: "Notifications",
    notificationsNote: "Choose how we send you trip reminders and booking updates.",
    emailNotifs: "Email notifications",
    smsNotifs: "SMS / text notifications",
    name: "Name",
    email: "Email",
    phone: "Phone",
    save: "Save",
    changePassword: "Change password",
    currentPassword: "Current password",
    newPassword: "New password",
    confirmNewPassword: "Confirm new password",
    updatePassword: "Update password",
    detailsUpdated: "Contact details updated",
    passwordsNoMatch: "New passwords don't match.",
    passwordUpdated: "Password updated",
  },
  customerProfile: {
    editProfile: "Edit your profile",
    viewPublic: "View public profile",
    city: "City",
    cityPlaceholder: "e.g. Boston, MA",
    aboutYou: "About you",
    bioPlaceholder: "Tell hosts a little about yourself…",
    saveChanges: "Save changes",
    profileUpdated: "Profile updated",
  },
  messages: {
    empty: "No conversations yet. Book a car to message the owner.",
    noMessages: "No messages yet",
    selectConversation: "Select a conversation to start chatting.",
  },
  chat: {
    empty: "No messages yet. Start the conversation.",
    placeholder: "Type a message…",
  },
  review: {
    title: "Review your trip",
    yourRating: "Your rating",
    commentPlaceholder: "Share details about your experience…",
    doubleBlind: "Reviews are double-blind — yours stays hidden until {{party}} submits theirs, or 14 days pass.",
    theOtherParty: "the other party",
    theHost: "the host",
    cancel: "Cancel",
    submit: "Submit review",
    chooseRating: "Please choose a star rating.",
    submittedToast: "Thanks! Your review was submitted.",
  },
  profileView: {
    host: "Host",
    verifiedHost: "Verified Host",
    joined: "Joined {{year}}",
    noReviews: "No published reviews yet.",
  },
  publicProfile: {
    notAvailable: "This profile isn't available.",
    vehiclesTitle: "Vehicles from this host",
    noVehicles: "No vehicles available from this host right now.",
    hostedBy: "Hosted by",
  },
  host: {
    shareProfile: "Share profile",
    profileCopied: "Profile link copied to clipboard.",
    overview: "Overview",
    listings: "Listings",
    trips: "Trips",
    calendar: "Calendar",
    maintenance: "Maintenance",
    payouts: "Payouts",
    messages: "Messages",
    reviews: "Reviews",
    settings: "Settings",
    hosting: "Hosting",
    becomeHost: "Become a host",
    becomeHostSub: "List your first vehicle to unlock your earnings dashboard, fleet calendar, and guest messaging.",
    listYourCar: "List your car",
  },
  hostOverview: {
    loading: "Loading your dashboard…",
    readyToPayOut: "Ready to pay out",
    pending: "Pending (trip in progress)",
    paidOut: "Paid out to date",
    approvedTotal: "Approved / total listings",
    trips: "Trips",
    upcoming: "Upcoming",
    activeNow: "Active now",
    allTime: "All-time",
    howPayouts: "How payouts work",
    payoutsDesc: "You keep <0>{{keep}}%</0> of the trip price (rental + unlimited miles). Belo's service fee is {{fee}}%. Payouts release after the trip ends and the car is returned, as long as there are no open claims.",
    underReview: "{{count}} under review",
    rejected: "{{count}} rejected",
  },
  hostListings: {
    fleetCount_one: "{{count}} vehicle in your fleet",
    fleetCount_other: "{{count}} vehicles in your fleet",
    addVehicle: "Add a vehicle",
    empty: "No vehicles yet",
    emptySub: "List your first car to start earning.",
    live: "Live",
    underReview: "Under review",
    rejected: "Rejected",
    paused: "Paused",
    perDaySeats: "${{rate}}/day · {{seats}} seats · {{transmission}}",
    reason: "Reason: {{reason}}",
    edit: "Edit",
    resume: "Resume",
    pause: "Pause",
    remove: "Remove",
    removeTitle: "Remove this listing?",
    removeConfirm: "\"{{name}}\" will be permanently removed from your fleet. This can't be undone.",
    cancel: "Cancel",
  },
  hostTrips: {
    upcoming: "upcoming",
    active: "active",
    history: "history",
    empty: "No {{tab}} trips.",
    guest: "Guest:",
    yourPayout: "Your payout",
    message: "Message {{name}}",
    guestFallback: "guest",
  },
  hostBlocks: {
    blockDates: "Block dates",
    selectVehicle: "Select a vehicle…",
    reasonOptional: "Reason (optional)",
    blockThese: "Block these dates",
    currentBlocks: "Current blocks",
    noBlocked: "No blocked dates.",
    booked: "Booked",
    turoSync: "Turo Sync",
    turoSyncDesc: "Booked your car on Turo (or elsewhere)? Block those dates here so guests can't double-book it on Belo Rentals.",
    turoReason: "Turo booking",
    blocked: "Blocked",
    blockedTooltip: "Blocked: {{car}}",
  },
  calendar: {
    months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    dow: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  },
  hostPayouts: {
    achTitle: "Bank payouts (ACH)",
    achSetup: "Direct-deposit payouts are being set up by Belo Rentals and will be available here shortly. Your earnings are tracked below in the meantime.",
    connectedEnabled: "Bank connected — payouts enabled",
    finishConnecting: "Finish connecting your bank",
    getPaidDirect: "Get paid by direct deposit",
    connectedDesc: "Your payouts are sent straight to your bank account via secure ACH transfer, powered by Stripe.",
    pendingDesc: "You started linking your bank but a few details are still needed before payouts can be sent.",
    connectDesc: "Securely link your bank account through Stripe so Belo Rentals can send your payouts via ACH.",
    manageBank: "Manage bank",
    finishSetup: "Finish setup",
    connectBank: "Connect bank",
    readyToPayOut: "Ready to pay out",
    pending: "Pending",
    paidOut: "Paid out",
    empty: "No earnings yet. Your payouts appear here once guests book your cars.",
    car: "Car",
    guest: "Guest",
    dates: "Dates",
    trip: "Trip",
    beloFee: "Belo fee",
    yourPayout: "Your payout",
    status: "Status",
    ready: "Ready",
    paid: "Paid",
    pendingStatus: "Pending",
  },
  hostMessages: {
    empty: "No trips to message about yet.",
    placeholder: "Select a trip to view the conversation.",
  },
  hostReviews: {
    rateGuests: "Rate your recent guests",
    review: "Review",
    fromGuests: "{{count}} reviews from guests",
    fromGuests_one: "{{count}} review from guests",
    empty: "No published reviews yet. Reviews appear once both you and the guest submit, or after 14 days.",
  },
  hostSettings: {
    hostType: "Host type",
    individual: "Individual",
    individualDesc: "You're renting out your personal vehicle(s).",
    business: "Business",
    businessDesc: "You operate a rental company or fleet.",
    businessName: "Business name",
    ein: "EIN",
    optional: "(optional)",
    contactPayouts: "Contact & payouts",
    phone: "Phone",
    address: "Address",
    payoutNote: "Payout note",
    payoutNoteHint: "(how you'd like to be paid)",
    payoutPlaceholder: "e.g. ACH to checking ****1234, or Zelle 978-555-0100",
    feeNote: "Belo's service fee is {{fee}}% of the trip (rental + unlimited miles). You keep the rest, paid after each completed trip.",
    saveSettings: "Save settings",
    savedToast: "Host settings saved",
  },
  listingDialog: {
    editListing: "Edit listing",
    reviewWarning: "Pricing, pickup and availability edits keep your listing live. Changing vehicle details (title, photos, specs) sends it back to \"Under review\" until we re-approve it.",
    rentalRates: "Rental rates",
    ratesHint: "Pick a category to load standard prices, then adjust any tier. You keep 100% of your rates.",
    weeklyPrice: "Weekly price ($)",
    oneWeek: "1 week",
    extraDay: "Extra day",
    pickupLocation: "Pickup location",
    pickupPlaceholder: "— Use my listing address —",
    listingTitle: "Listing title",
    weeklyRate: "Weekly rate ($)",
    deposit: "Deposit ($)",
    seats: "Seats",
    transmission: "Transmission",
    fuel: "Fuel",
    photoUrl: "Photo URL",
    description: "Description",
    features: "Features",
    featuresHint: "(comma-separated)",
    featuresPlaceholder: "Bluetooth, Backup camera, Apple CarPlay",
    cancel: "Cancel",
    saveChanges: "Save changes",
  },
  onboarding: {
    stepAbout: "About you",
    stepCar: "Your car",
    stepReview: "Review & list",
    tierEconomy: "Economy & sedans",
    tierSUV: "SUVs & trucks",
    tierPremium: "Premium (e.g. BMW X5)",
    perMo: "/mo",
    becomeHost: "Become a Host",
    listYourCar: "List your car",
    intro: "Earn money sharing your vehicle on Belo Rentals. Any make, model, year, or mileage — no vehicle restrictions.",
    fullName: "Full name",
    fullNamePlaceholder: "Jane Driver",
    email: "Email",
    emailPlaceholder: "you@email.com",
    mobilePhone: "Mobile phone",
    phonePlaceholder: "(978) 555-0142",
    verified: "Verified",
    sending: "Sending…",
    resend: "Resend",
    sendCode: "Send code",
    enterCode: "Enter the 6-digit code",
    verifying: "Verifying…",
    verify: "Verify",
    devCode: "Dev mode: your code is <0>{{code}}</0> (SMS delivery pending carrier registration).",
    continue: "Continue",
    vehicleVin: "Vehicle VIN",
    vinPlaceholder: "17-character VIN",
    decode: "Decode",
    qualifies: "Qualifies",
    tooOld: "Too old",
    tooOldDetail: "Vehicles must be {{years}} years or newer ({{from}} or later).",
    trim: "Trim",
    trimPlaceholder: "e.g. EX-L, Sport",
    earnEstimate: "Cars like yours earn <0>${{low}}–${{high}}/mo</0>",
    odometer: "Odometer (miles)",
    odometerPlaceholder: "e.g. 84000",
    odoError: "Must be under 130,000 miles.",
    weeklyRate: "Weekly rate ($)",
    vehicleType: "Vehicle type",
    photos: "Photos",
    photosHint: "— at least 5 clear shots (exterior, interior, dashboard)",
    address: "Vehicle / pickup address",
    addressPlaceholder: "Start typing your address…",
    addressFallback: "123 Main St, City, ST",
    back: "Back",
    vehicle: "Vehicle",
    host: "Host",
    weeklyRateLabel: "Weekly rate",
    perWeek: "${{rate}}/week",
    certify: "I certify that I have paid all applicable sales taxes on this vehicle, that I am authorized to list it, and that the information provided is accurate and complete.",
    submitting: "Submitting…",
    submitListing: "Submit listing",
    underReviewTitle: "Your car is under review",
    underReviewSub: "Our team typically reviews new listings within 1–2 business days, then it goes live on the Belo Rentals fleet.",
    myAccount: "My Account",
    browseFleet: "Browse the Fleet",
    enterPhoneFirst: "Enter your phone number first.",
    codeSent: "Verification code sent to your phone.",
    enter6Digit: "Enter the 6-digit code.",
    phoneVerifiedToast: "Phone verified!",
    enterVin: "Enter your VIN.",
    certifyError: "Please certify the tax statement to continue.",
    contactHint: "How can we reach you about your listing and payouts?",
    docsTitle: "Insurance & registration",
    docsHint: "Upload a clear photo of each document.",
    insurance: "Vehicle insurance",
    registration: "Vehicle registration",
    uploadPhoto: "Upload photo",
    change: "Change",
    uploaded: "Uploaded",
    payoutTitle: "Payout details (ACH bank transfer)",
    payoutHint: "Where should we send your earnings? You keep 100% of the trip price.",
    accountHolder: "Account holder name",
    routingNumber: "Routing number (9 digits)",
    accountNumber: "Account number",
    accountType: "Account type",
    checking: "Checking",
    savings: "Savings",
    agreementStep: "Marketplace Agreement",
    agreementTitle: "Host Marketplace Agreement",
    agreementIntro: "Please read and e-sign the Belo Rentals Host Marketplace Agreement to list your car.",
    agreementSignLabel: "Type your full legal name to sign",
    agreementAgree: "I have read and agree to the Belo Rentals Host Marketplace Agreement. I understand Belo charges guests a service fee and that I keep 100% of my trip earnings.",
    agreementLoading: "Loading agreement…",
    agreementError: "Please read and e-sign the agreement to continue.",
    payoutError: "Please enter valid ACH payout details.",
    docsError: "Please upload your insurance and registration photos.",
    goToHostPortal: "Go to Host Portal",
    keep85: "You keep 100% of the trip price.",
  },
};

const pt = {
  common: {
    back: "Voltar",
    cancel: "Cancelar",
    save: "Salvar",
    saveChanges: "Salvar alterações",
    loading: "Carregando…",
    remove: "Remover",
    edit: "Editar",
    submit: "Enviar",
    days_one: "{{count}} dia",
    days_other: "{{count}} dias",
    daysShort: "{{count}}d",
    reviews_one: "{{count}} avaliação",
    reviews_other: "{{count}} avaliações",
  },
  tripPhotos: {
    needMin: "Adicione pelo menos {{min}} fotos.",
    saved: "Fotos salvas.",
    saving: "Salvando…",
    submit: "Salvar fotos",
    retention: "As fotos são mantidas por 5 dias após o fim da viagem e depois excluídas automaticamente — a menos que haja uma reclamação em aberto.",
    host_pre: { title: "Fotos pré-viagem", hint: "Documente o estado do carro antes da entrega (mín. {{min}})." },
    customer_start: { title: "Iniciar sua viagem", hint: "Adicione ao menos {{min}} fotos do carro na retirada para iniciar a viagem." },
    customer_return: { title: "Fotos de devolução", hint: "Fotografe o estado do carro na devolução (mín. {{min}})." },
    host_return: { title: "Inspeção de devolução", hint: "Documente o estado do carro após a viagem (mín. {{min}})." },
    addStart: "Iniciar viagem · fotos",
    started: "Viagem iniciada",
    addReturn: "Fotos de devolução",
    returnDone: "Fotos de devolução adicionadas",
    preTrip: "Fotos pré-viagem",
    preTripDone: "Fotos pré-viagem adicionadas",
    returnInspect: "Inspeção de devolução",
    view: "Ver fotos",
    purged: "Fotos removidas (fim do período de retenção)",
  },
  nav: {
    home: "Início",
    fleet: "Veículos",
    contact: "Contato",
    hostYourCar: "Anuncie seu carro",
    hostPortal: "Portal do Anfitrião",
    signIn: "Entrar",
    bookNow: "Reservar",
    account: "Conta",
    myTrips: "Minhas Viagens",
    signOut: "Sair",
    renting: "Alugando",
    hosting: "Anunciando",
  },
  footer: {
    tagline: "Aluguel de carros acessível em Malden e Clinton, MA. Tarifas diárias e semanais flexíveis, além de entrega no Aeroporto Logan.",
    airportBadge: "Entrega no Aeroporto Logan",
    navigate: "Navegação",
    ourFleet: "Veículos",
    contact: "Contato",
    admin: "Admin",
    reachUs: "Fale Conosco",
    maldenLocation: "Unidade Malden",
    clintonLocation: "Unidade Clinton",
    rights: "Todos os direitos reservados.",
  },
  home: {
    heroBadge: "Agora com entrega no Aeroporto Logan",
    heroTitle1: "Aluguel de carros acessível,",
    heroTitle2: "de forma simples.",
    heroSubtitle: "Veículos confiáveis e econômicos com tarifas diárias flexíveis",
    heroSubtitlePrice: "a partir de US$ 50/dia",
    heroSubtitleEnd: ". Reserve on-line em minutos.",
    browseFleet: "Ver Veículos",
    statDailyRate: "Tarifa diária a partir de",
    statBooking: "Reserva on-line",
    statAirport: "Retirada e Devolução",
    statAirportValue: "Aeroporto Logan",
    featureRatesTitle: "Tarifas Flexíveis",
    featureRatesDesc: "Tarifas a partir de US$ 35/dia ao reservar uma semana inteira.",
    featureBookingTitle: "Reserva Rápida",
    featureBookingDesc: "Reserve on-line em menos de dois minutos com confirmação instantânea.",
    featureReliableTitle: "Confiável e Segurado",
    featureReliableDesc: "Veículos bem conservados e econômicos com os quais você pode contar.",
    airportService: "Serviço de Aeroporto",
    airportDropOff: "Serviço de Entrega",
    airportTitle: "Vai viajar? Cuidamos da entrega.",
    airportDesc: "Vai chegar ou sair de Boston? Retirada e devolução no Aeroporto Logan (BOS) disponíveis — os mesmos carros limpos e bem conservados, prontos quando você chegar.",
    airportPoint1: "Retirada e entrega práticas no estacionamento econômico do Aeroporto Logan",
    airportPoint2: "Diversas opções de aluguel de carros econômicos",
    airportPoint3: "Avaliação 5 estrelas no Turo",
    airportCta: "Ver entrega no aeroporto",
    fleetLabel: "Nossos Veículos",
    fleetTitle: "Os Modelos",
    viewAll: "Ver Todos",
    ctaTitle: "Prontos quando você estiver.",
    ctaDesc: "Escolha seu carro e escolha suas datas.",
    ctaButton: "Reservar Agora",
  },
  fleet: {
    checkAvailability: "Verifique a disponibilidade para suas datas",
    pickupDate: "Data de retirada",
    pickupTime: "Hora de retirada",
    returnDate: "Data de devolução",
    returnTime: "Hora de devolução",
    pickup: "Retirada",
    return: "Devolução",
    search: "Buscar",
    clear: "Limpar",
    showing: "Mostrando veículos disponíveis",
    loading: "Carregando frota…",
    emptyFiltered: "Nenhum veículo disponível para essas datas. Tente outro período.",
    emptyCategory: "Ainda não há veículos nesta categoria.",
    all: "Todos",
    sortBy: "Ordenar por",
    sortPriceAsc: "Preço: menor para maior",
    sortPriceDesc: "Preço: maior para menor",
    sortNearest: "Mais perto de mim",
    geoLocating: "Buscando veículos mais próximos de você…",
    geoOk: "Ordenado por distância da sua localização.",
    geoDenied: "Localização indisponível — mostrando todos os veículos. Ative o acesso à localização para ordenar por proximidade.",
    vehicle: "veículo",
    vehicles: "veículos",
  },
  inspection: {
    needAll: "Adicione todas as fotos obrigatórias antes de enviar.",
    saved: "Inspeção salva — cada foto tem carimbo de data/hora e fica armazenada com a viagem.",
    photosRequired: "fotos obrigatórias",
    submit: "Enviar inspeção",
    protect: "Estas fotos com carimbo de data/hora protegem você e o anfitrião. Elas ficam armazenadas com a reserva e são revisadas em caso de disputa.",
    customer_start: {
      title: "Check-in do veículo",
      hint: "Tire as 9 fotos antes de sair — sua viagem não pode começar sem elas.",
    },
    customer_return: {
      title: "Check-out do veículo",
      hint: "Tire as mesmas 9 fotos na devolução para liberar seu depósito rapidamente.",
    },
    angles: {
      front: "Frente do veículo",
      rear: "Traseira do veículo",
      driver_side: "Lado do motorista",
      passenger_side: "Lado do passageiro",
      wheels: "Todas as rodas",
      interior: "Interior",
      dashboard: "Painel",
      odometer: "Hodômetro",
      fuel_level: "Nível de combustível",
    },
  },
  veriff: {
    title: "Verificação de identidade",
    intro: "Para a segurança de todos, verificamos cada locatário uma única vez com a carteira de motorista e uma selfie rápida. Leva cerca de 2 minutos.",
    profileIntro: "Verifique uma vez com sua carteira de motorista e uma selfie — depois reserve qualquer veículo instantaneamente.",
    start: "Iniciar verificação",
    starting: "Preparando sua verificação segura…",
    checking: "Conferindo o resultado da sua verificação…",
    approved: "Você foi verificado! Continuando…",
    underReview: "Sua verificação foi enviada e está em análise. Enviaremos um e-mail assim que for aprovada — sua reserva está salva.",
    declined: "Não conseguimos verificar sua identidade. Tente novamente ou ligue para 978-221-1451.",
    error: "Não foi possível iniciar a verificação. Tente novamente.",
    tryAgain: "Tentar novamente",
    verifiedBadge: "Identidade Verificada",
    notVerified: "Ainda não verificado",
    verifyNow: "Verificar agora",
    stepNote: "Verificação de identidade única e rápida antes do pagamento (~2 min)",
    signInFirst: "Entre ou crie uma conta para reservar — todo locatário passa por verificação de identidade uma única vez.",
    createAccountToContinue: "Falta pouco — crie sua conta grátis para garantir sua reserva.",
    reverifyNeeded: "Precisamos que você verifique sua identidade novamente antes da próxima reserva.",
    addDriver: "Adicionar um motorista adicional",
    additionalDriverNote: "O motorista adicional receberá um e-mail para verificar a identidade antes do início da viagem.",
    driverName: "Nome completo do motorista",
    driverEmail: "E-mail do motorista",
    driverIncomplete: "Informe o nome e o e-mail do motorista adicional.",
    driverPageTitle: "Verificação do motorista",
    driverPageIntro: "{{customer}} adicionou você como motorista adicional nesta viagem da Belo Rentals. Verifique sua identidade para ser aprovado.",
    driverLabel: "Motorista adicional",
    driverVerified: "Você foi verificado! Está aprovado como motorista adicional desta viagem.",
    legalName: "Nome legal",
    dob: "Data de nascimento",
    licenseNumber: "Número da carteira",
    issuedBy: "Emitida por",
    expires: "Validade",
    verifiedOn: "Verificado em",
  },
  carCard: {
    perDay: "/dia",
    beforeTaxes: "antes dos impostos",
    fromWeek: "a partir de · US$ {{rate}}/semana",
    refundableDeposit: "+ US$ {{deposit}} de caução reembolsável",
    stock: "Estoque",
    details: "Detalhes",
    book: "Reservar",
    unavailable: "Indisponível",
    removeSaved: "Remover dos salvos",
    saveForLater: "Salvar para depois",
  },
  carDetail: {
    notFound: "Este veículo não foi encontrado.",
    backToFleet: "Voltar para a Frota",
    confirmingPayment: "Confirmando seu pagamento…",
    dontClose: "Por favor, não feche esta janela.",
    perDayFrom: "/ dia — a partir de",
    refundableDeposit: "de caução reembolsável",
    viewPhotos: "Ver Fotos",
    rentalRates: "Tarifas de Aluguel",
    fromPerDay: "a partir de",
    week: "1 semana",
    sevenDays: "(7 dias)",
    extraDay: "Cada dia extra após uma semana",
    mileageLabel: "Quilometragem:",
    mileageDetail: "{{perDay}} mi/dia · {{perWeek}} mi/semana · {{perMonth}} mi/mês incluídas. US$ {{overage}}/mi de excedente, ou adicione quilometragem ilimitada por US$ {{unlimited}}/dia.",
    depositDetail: "US$ {{deposit}} de caução reembolsável — devolvida após uma devolução limpa, com tanque cheio, sem danos e no prazo.",
    bestValue: "Melhor valor",
    ratesDisclaimer: "As tarifas exibidas são preços iniciais e podem variar de acordo com o veículo, disponibilidade, datas de aluguel, local de retirada, demanda sazonal e outros fatores. Impostos, taxas de serviço, cauções, produtos opcionais de proteção e cobranças adicionais não estão incluídos nas tarifas exibidas.",
    browseVehicles: "Ver Veículos Disponíveis",
    vehicleDetails: "Detalhes do veículo",
    seats: "Assentos",
    transmission: "Câmbio",
    fuel: "Combustível",
    mileage: "Quilometragem",
    category: "Categoria",
    miUnit: "mi",
    stockHash: "Estoque nº",
    vin: "Chassi (VIN)",
    aboutCar: "Sobre este carro",
    features: "Recursos",
    airportNote: "Entrega no Aeroporto Logan disponível no painel de reserva",
    days: "dias",
  },
  booking: {
    dueNow: "a pagar agora",
    perDayFrom: "/ dia — a partir de",
    inclDeposit: "incl. US$ {{deposit}} de caução reembolsável",
    pickup: "Retirada",
    return: "Devolução",
    pickupDate: "Data de retirada",
    returnDate: "Data de devolução",
    dob: "Data de Nascimento",
    youngDriverNote: "Menor de 25 — aplica-se uma taxa de motorista jovem de US$ {{fee}}.",
    pickupLocation: "Local de Retirada",
    pickupTbd: "Informado após a reserva",
    milesFromLogan: "a 7 milhas do Aeroporto Logan",
    cancellationPolicy: "Política de Cancelamento",
    refundable: "Reembolsável",
    refundableDesc: "Cancele com reembolso total do aluguel.",
    nonRefundable: "Não reemb.",
    off10: "10% OFF",
    nonRefDesc: "Economize 10%, sem reembolso.",
    nonRefIneligible: "Reserve com {{days}}+ dias de antecedência.",
    airportOption: "Retirada e Devolução no Aeroporto Logan",
    airportOptionDesc: "Taxa fixa para retirar e devolver no Boston Logan.",
    unlimitedMiles: "Quilometragem ilimitada",
    unlimitedMilesDesc: "Esqueça o limite de {{miles}} mi/dia — dirija o quanto quiser.",
    perDay: "/dia",
    discountCode: "Código de Desconto",
    enterCode: "Digite o código",
    apply: "Aplicar",
    yourDetails: "Seus Dados",
    fullName: "Nome Completo",
    email: "E-mail",
    phone: "Telefone",
    notesOptional: "Observações (opcional)",
    notesPlaceholder: "Algum pedido especial…",
    unavailableWarning: "Este veículo está indisponível para reserva no momento.",
    overlapWarning: "Suas datas coincidem com datas indisponíveis. Escolha outro período.",
    summaryPlaceholder: "Selecione suas datas e a data de nascimento para ver o preço total.",
    duration: "Duração",
    milesIncluded: "Quilometragem incluída",
    unlimited: "Ilimitada",
    rental: "Aluguel",
    rentalBreakdown: "Detalhamento do aluguel",
    subtotal: "Subtotal ({{count}} dias)",
    subtotal_one: "Subtotal ({{count}} dia)",
    nonRefDiscount: "Não reembolsável (−10%)",
    serviceFee: "Taxa de serviço ({{pct}}%)",
    tax: "Imposto",
    youngDriverFee: "Taxa de motorista jovem",
    airportDropoff: "Entrega no aeroporto",
    discount: "Desconto ({{code}})",
    tripCost: "Custo da viagem",
    tripCostNote: "(aluguel + imposto)",
    refundableDepositRow: "Caução reembolsável",
    depositNote: "Retida durante sua viagem — devolvida a você depois.",
    payNow: "Pagar agora",
    redirecting: "Redirecionando para o checkout seguro…",
    reservePay: "Reservar e Pagar {{amount}}",
    reservePayShort: "Reservar e Pagar",
    reserve: "Reservar",
    securePayment: "Pagamento seguro via Stripe. Caução de US$ {{deposit}} totalmente reembolsável.",
    completeDetails: "Complete os dados da sua viagem para reservar.",
    completeRental: "Por favor, complete os dados do aluguel.",
  },
  confirm: {
    title: "Reserva Confirmada",
    thankYou: "Obrigado, {{name}}. Seu pagamento de {{amount}} foi recebido e sua reserva está confirmada. Entraremos em contato pelo telefone {{phone}} com os detalhes da retirada.",
    vehicle: "Veículo",
    pickupLocation: "Local de retirada",
    pickup: "Retirada",
    return: "Devolução",
    duration: "Duração",
    mileage: "Quilometragem",
    unlimited: "Ilimitada",
    milesIncluded: "{{miles}} mi incluídas",
    rental: "Aluguel",
    rentalNonRef: "Aluguel (não reembolsável −10%)",
    serviceFee: "Taxa de serviço ({{pct}}%)",
    tax: "Imposto",
    youngDriverFee: "Taxa de motorista jovem",
    airportRow: "Retirada e Devolução no Aeroporto Logan",
    unlimitedMiles: "Quilometragem ilimitada",
    discount: "Desconto ({{code}})",
    refundableDeposit: "Caução reembolsável",
    paid: "Pago",
    totalPaid: "Total Pago",
    agreementSigned: "Contrato de aluguel assinado por {{name}} — uma cópia foi enviada por e-mail a você.",
    browseMore: "Ver Mais",
    home: "Início",
  },
  agreement: {
    title: "Contrato de Aluguel",
    review: "Revise os termos e assine para continuar até o pagamento seguro.",
    vehicle: "Veículo",
    renter: "Locatário",
    tripStart: "Início da viagem",
    tripEnd: "Fim da viagem",
    pickupReturn: "Retirada e devolução",
    mileage: "Quilometragem",
    unlimited: "Ilimitada",
    milesIncluded: "{{miles}} mi incluídas",
    refundableDeposit: "Caução reembolsável",
    totalDueNow: "Total a pagar agora",
    prohibited: "Usos estritamente proibidos",
    loadingAgreement: "Carregando contrato…",
    signLabel: "Digite seu nome legal completo para assinar",
    signPlaceholder: "Nome legal completo",
    agreeText: "Eu, <0>{{name}}</0>, li e concordo com o Contrato de Aluguel da Belo Rentals. Entendo que a caução é reembolsável somente se todas as condições de devolução forem atendidas e que o uso para transporte por aplicativo/aluguel é proibido. Assinado eletronicamente em {{date}}.",
    cancel: "Cancelar",
    redirecting: "Redirecionando…",
    signPay: "Assinar e Pagar {{amount}}",
  },
  contact: {
    label: "Entre em contato",
    title: "Vamos conversar",
    subtitle: "Dúvidas sobre um aluguel ou sobre nosso serviço de entrega no Aeroporto Logan? Fale conosco — teremos prazer em ajudar.",
    phone: "Telefone",
    email: "E-mail",
    maldenLocation: "Unidade Malden",
    clintonLocation: "Unidade Clinton",
    airportService: "Serviço de Aeroporto",
    airportAvailable: "Entrega no Aeroporto Logan disponível",
    name: "Nome",
    namePlaceholder: "João Silva",
    phonePlaceholder: "(978) 000-0000",
    emailPlaceholder: "voce@email.com",
    message: "Mensagem",
    messagePlaceholder: "Como podemos ajudar?",
    sending: "Enviando…",
    sendMessage: "Enviar Mensagem",
    sentToast: "Mensagem enviada! Entraremos em contato em breve.",
  },
  auth: {
    welcomeBack: "Bem-vindo de volta",
    signInSub: "Entre para gerenciar suas reservas.",
    email: "E-mail",
    password: "Senha",
    forgotPassword: "Esqueceu a senha?",
    signingIn: "Entrando…",
    signIn: "Entrar",
    newHere: "Novo na Belo Rentals?",
    createAccount: "Criar uma conta",
    loginFailed: "Falha no login",
    createTitle: "Crie sua conta",
    createSub: "Acompanhe suas viagens e reserve mais rápido na próxima vez.",
    checkoutHandoffSub: "Último passo — defina uma senha para concluir sua reserva. Preenchemos o resto.",
    fullName: "Nome Completo",
    phone: "Telefone",
    phoneInvalid: "Digite um número de telefone dos EUA válido.",
    passwordMin: "Pelo menos 8 caracteres",
    creating: "Criando conta…",
    createButton: "Criar Conta",
    haveAccount: "Já tem uma conta?",
    signInLink: "Entrar",
    registerFailed: "Falha no cadastro",
    resetTitle: "Redefinir senha",
    resetSub: "Digite seu e-mail e enviaremos um link de redefinição.",
    sending: "Enviando…",
    sendResetLink: "Enviar Link de Redefinição",
    backToSignIn: "Voltar para o login",
    checkEmail: "Verifique seu e-mail",
    checkEmailSub: "Se existir uma conta para <0>{{email}}</0>, enviamos um link para redefinir sua senha. O link expira em 1 hora.",
    invalidLink: "Link inválido",
    invalidLinkSub: "Este link de redefinição está ausente ou inválido.",
    requestNew: "Solicitar um novo link",
    passwordUpdated: "Senha atualizada",
    redirectingSignIn: "Redirecionando você para o login…",
    setNewPassword: "Definir uma nova senha",
    setNewSub: "Escolha uma senha forte que você vá lembrar.",
    newPassword: "Nova Senha",
    confirmPassword: "Confirmar Senha",
    reenterPassword: "Digite a senha novamente",
    updating: "Atualizando…",
    updatePassword: "Atualizar Senha",
    passwordsNoMatch: "As senhas não coincidem.",
    passwordUpdatedToast: "Senha atualizada! Por favor, entre.",
  },
  account: {
    dashboard: "Painel",
    trips: "Minhas Viagens",
    messages: "Mensagens",
    saved: "Salvos",
    receipts: "Recibos",
    profile: "Perfil",
    settings: "Configurações",
    signOut: "Sair",
  },
  dashboard: {
    tripInProgress: "Viagem em andamento",
    nextTrip: "Sua próxima viagem · em {{days}} dias",
    noUpcoming: "Nenhuma viagem futura",
    noUpcomingSub: "Explore a frota e reserve sua próxima viagem.",
    browseFleet: "Ver frota",
    viewTrip: "Ver detalhes da viagem",
    upcomingTrips: "Viagens futuras",
    activeNow: "Ativas agora",
    completed: "Concluídas",
    totalSpent: "Total gasto",
    reviewNudge_one: "Você tem {{count}} viagem aguardando avaliação.",
    reviewNudge_other: "Você tem {{count}} viagens aguardando avaliação.",
    reviewCar: "Avaliar {{car}}",
  },
  trips: {
    upcoming: "futuras",
    active: "ativas",
    completed: "concluídas",
    cancelled: "canceladas",
    searchPlaceholder: "Buscar por carro…",
    empty: "Nenhuma viagem {{tab}}.",
    paid: "Pago",
    unpaid: "Não pago",
    details: "Detalhes",
    messageOwner: "Enviar mensagem ao proprietário",
    leaveReview: "Deixar uma avaliação",
    rental: "Aluguel",
    tax: "Imposto",
    serviceFee: "Taxa de serviço ({{pct}}%)",
    unlimitedMiles: "Quilometragem ilimitada",
    youngDriverFee: "Taxa de motorista jovem",
    airportDropoff: "Entrega no aeroporto",
    discount: "Desconto ({{code}})",
    refundableDeposit: "Caução reembolsável",
    totalPaid: "Total pago",
    mileage: "Quilometragem",
    unlimited: "Ilimitada",
    agreement: "Contrato",
    signedBy: "Assinado por {{name}}",
  },
  receipts: {
    empty: "Ainda não há recibos",
    emptySub: "Reservas pagas mostrarão um recibo para download aqui.",
    vehicle: "Veículo",
    dates: "Datas",
    total: "Total",
    receipt: "Recibo",
    downloadError: "Não foi possível baixar o recibo.",
  },
  saved: {
    empty: "Ainda não há carros salvos",
    emptySub: "Toque no coração de qualquer carro para salvá-lo para depois.",
    browseFleet: "Explorar a frota",
    perDay: "/dia",
    book: "Reservar",
  },
  customerSettings: {
    account: "Conta",
    paymentMethods: "Formas de pagamento",
    addCard: "Adicionar cartão",
    redirecting: "Redirecionando…",
    cardsNote: "Os cartões são armazenados com segurança pela Stripe — a Belo Rentals nunca vê o número do seu cartão.",
    noCards: "Nenhum cartão salvo ainda. Adicione um para pagar mais rápido na próxima vez.",
    cardSaved: "Cartão salvo! Ele estará disponível no seu próximo checkout.",
    cardRemoved: "Cartão removido.",
    defaultUpdated: "Cartão padrão atualizado.",
    default: "Padrão",
    expires: "Validade",
    makeDefault: "Definir como padrão",
    remove: "Remover",
    notifications: "Notificações",
    notificationsNote: "Escolha como enviamos lembretes de viagem e atualizações de reserva.",
    emailNotifs: "Notificações por e-mail",
    smsNotifs: "Notificações por SMS",
    name: "Nome",
    email: "E-mail",
    phone: "Telefone",
    save: "Salvar",
    changePassword: "Alterar senha",
    currentPassword: "Senha atual",
    newPassword: "Nova senha",
    confirmNewPassword: "Confirmar nova senha",
    updatePassword: "Atualizar senha",
    detailsUpdated: "Dados de contato atualizados",
    passwordsNoMatch: "As novas senhas não coincidem.",
    passwordUpdated: "Senha atualizada",
  },
  customerProfile: {
    editProfile: "Editar seu perfil",
    viewPublic: "Ver perfil público",
    city: "Cidade",
    cityPlaceholder: "ex.: Boston, MA",
    aboutYou: "Sobre você",
    bioPlaceholder: "Conte um pouco sobre você aos anfitriões…",
    saveChanges: "Salvar alterações",
    profileUpdated: "Perfil atualizado",
  },
  messages: {
    empty: "Ainda não há conversas. Reserve um carro para falar com o proprietário.",
    noMessages: "Ainda não há mensagens",
    selectConversation: "Selecione uma conversa para começar a conversar.",
  },
  chat: {
    empty: "Ainda não há mensagens. Inicie a conversa.",
    placeholder: "Digite uma mensagem…",
  },
  review: {
    title: "Avalie sua viagem",
    yourRating: "Sua avaliação",
    commentPlaceholder: "Compartilhe detalhes sobre sua experiência…",
    doubleBlind: "As avaliações são duplo-cegas — a sua fica oculta até que {{party}} envie a dele, ou passem 14 dias.",
    theOtherParty: "a outra parte",
    theHost: "o anfitrião",
    cancel: "Cancelar",
    submit: "Enviar avaliação",
    chooseRating: "Por favor, escolha uma classificação por estrelas.",
    submittedToast: "Obrigado! Sua avaliação foi enviada.",
  },
  profileView: {
    host: "Anfitrião",
    verifiedHost: "Anfitrião Verificado",
    joined: "Entrou em {{year}}",
    noReviews: "Ainda não há avaliações publicadas.",
  },
  publicProfile: {
    notAvailable: "Este perfil não está disponível.",
    vehiclesTitle: "Veículos deste anfitrião",
    noVehicles: "Nenhum veículo disponível deste anfitrião no momento.",
    hostedBy: "Anfitrião",
  },
  host: {
    shareProfile: "Compartilhar perfil",
    profileCopied: "Link do perfil copiado para a área de transferência.",
    overview: "Visão Geral",
    listings: "Anúncios",
    trips: "Viagens",
    calendar: "Calendário",
    maintenance: "Manutenção",
    payouts: "Pagamentos",
    messages: "Mensagens",
    reviews: "Avaliações",
    settings: "Configurações",
    hosting: "Anunciando",
    becomeHost: "Torne-se um anfitrião",
    becomeHostSub: "Anuncie seu primeiro veículo para desbloquear seu painel de ganhos, calendário da frota e mensagens com hóspedes.",
    listYourCar: "Anuncie seu carro",
  },
  hostOverview: {
    loading: "Carregando seu painel…",
    readyToPayOut: "Pronto para pagamento",
    pending: "Pendente (viagem em andamento)",
    paidOut: "Pago até o momento",
    approvedTotal: "Anúncios aprovados / total",
    trips: "Viagens",
    upcoming: "Futuras",
    activeNow: "Ativas agora",
    allTime: "Total",
    howPayouts: "Como funcionam os pagamentos",
    payoutsDesc: "Você fica com <0>{{keep}}%</0> do valor da viagem (aluguel + quilometragem ilimitada). A taxa de serviço da Belo é {{fee}}%. Os pagamentos são liberados após o fim da viagem e a devolução do carro, desde que não haja reivindicações em aberto.",
    underReview: "{{count}} em análise",
    rejected: "{{count}} rejeitados",
  },
  hostListings: {
    fleetCount_one: "{{count}} veículo na sua frota",
    fleetCount_other: "{{count}} veículos na sua frota",
    addVehicle: "Adicionar um veículo",
    empty: "Ainda não há veículos",
    emptySub: "Anuncie seu primeiro carro para começar a ganhar.",
    live: "No ar",
    underReview: "Em análise",
    rejected: "Rejeitado",
    paused: "Pausado",
    perDaySeats: "US$ {{rate}}/dia · {{seats}} assentos · {{transmission}}",
    reason: "Motivo: {{reason}}",
    edit: "Editar",
    resume: "Retomar",
    pause: "Pausar",
    remove: "Remover",
    removeTitle: "Remover este anúncio?",
    removeConfirm: "\"{{name}}\" será removido permanentemente da sua frota. Isso não pode ser desfeito.",
    cancel: "Cancelar",
  },
  hostTrips: {
    upcoming: "futuras",
    active: "ativas",
    history: "histórico",
    empty: "Nenhuma viagem {{tab}}.",
    guest: "Hóspede:",
    yourPayout: "Seu pagamento",
    message: "Enviar mensagem a {{name}}",
    guestFallback: "hóspede",
  },
  hostBlocks: {
    blockDates: "Bloquear datas",
    selectVehicle: "Selecione um veículo…",
    reasonOptional: "Motivo (opcional)",
    blockThese: "Bloquear estas datas",
    currentBlocks: "Bloqueios atuais",
    noBlocked: "Nenhuma data bloqueada.",
    booked: "Reservado",
    blocked: "Bloqueado",
    blockedTooltip: "Bloqueado: {{car}}",
    turoSync: "Sincronização Turo",
    turoSyncDesc: "Reservou seu carro no Turo (ou em outro lugar)? Bloqueie essas datas aqui para que os hóspedes não façam reserva dupla na Belo Rentals.",
    turoReason: "Reserva do Turo",
  },
  calendar: {
    months: ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"],
    dow: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
  },
  hostPayouts: {
    achTitle: "Pagamentos bancários (ACH)",
    achSetup: "Os pagamentos por depósito direto estão sendo configurados pela Belo Rentals e estarão disponíveis aqui em breve. Enquanto isso, seus ganhos são acompanhados abaixo.",
    connectedEnabled: "Banco conectado — pagamentos ativados",
    finishConnecting: "Conclua a conexão do seu banco",
    getPaidDirect: "Receba por depósito direto",
    connectedDesc: "Seus pagamentos são enviados diretamente para sua conta bancária via transferência ACH segura, com tecnologia Stripe.",
    pendingDesc: "Você começou a vincular seu banco, mas alguns dados ainda são necessários antes que os pagamentos possam ser enviados.",
    connectDesc: "Vincule sua conta bancária com segurança através do Stripe para que a Belo Rentals possa enviar seus pagamentos via ACH.",
    manageBank: "Gerenciar banco",
    finishSetup: "Concluir configuração",
    connectBank: "Conectar banco",
    readyToPayOut: "Pronto para pagamento",
    pending: "Pendente",
    paidOut: "Pago",
    empty: "Ainda não há ganhos. Seus pagamentos aparecem aqui assim que os hóspedes reservarem seus carros.",
    car: "Carro",
    guest: "Hóspede",
    dates: "Datas",
    trip: "Viagem",
    beloFee: "Taxa Belo",
    yourPayout: "Seu pagamento",
    status: "Status",
    ready: "Pronto",
    paid: "Pago",
    pendingStatus: "Pendente",
  },
  hostMessages: {
    empty: "Ainda não há viagens para conversar.",
    placeholder: "Selecione uma viagem para ver a conversa.",
  },
  hostReviews: {
    rateGuests: "Avalie seus hóspedes recentes",
    review: "Avaliar",
    fromGuests: "{{count}} avaliações de hóspedes",
    fromGuests_one: "{{count}} avaliação de hóspedes",
    empty: "Ainda não há avaliações publicadas. As avaliações aparecem quando você e o hóspede enviam, ou após 14 dias.",
  },
  hostSettings: {
    hostType: "Tipo de anfitrião",
    individual: "Pessoa física",
    individualDesc: "Você aluga seu(s) veículo(s) pessoal(is).",
    business: "Empresa",
    businessDesc: "Você opera uma locadora ou frota.",
    businessName: "Nome da empresa",
    ein: "CNPJ/EIN",
    optional: "(opcional)",
    contactPayouts: "Contato e pagamentos",
    phone: "Telefone",
    address: "Endereço",
    payoutNote: "Observação de pagamento",
    payoutNoteHint: "(como você prefere receber)",
    payoutPlaceholder: "ex.: ACH para conta ****1234, ou Zelle 978-555-0100",
    feeNote: "A taxa de serviço da Belo é {{fee}}% da viagem (aluguel + quilometragem ilimitada). Você fica com o restante, pago após cada viagem concluída.",
    saveSettings: "Salvar configurações",
    savedToast: "Configurações do anfitrião salvas",
  },
  listingDialog: {
    editListing: "Editar anúncio",
    reviewWarning: "Edições de preço, retirada e disponibilidade mantêm seu anúncio ativo. Alterar os detalhes do veículo (título, fotos, especificações) o envia de volta para \"Em análise\" até a reaprovação.",
    rentalRates: "Tarifas de aluguel",
    ratesHint: "Escolha uma categoria para carregar os preços padrão e ajuste qualquer faixa. Você fica com 100% das suas tarifas.",
    weeklyPrice: "Preço semanal ($)",
    oneWeek: "1 semana",
    extraDay: "Dia extra",
    pickupLocation: "Local de retirada",
    pickupPlaceholder: "— Usar o endereço do meu anúncio —",
    listingTitle: "Título do anúncio",
    weeklyRate: "Tarifa semanal (US$)",
    deposit: "Caução (US$)",
    seats: "Assentos",
    transmission: "Câmbio",
    fuel: "Combustível",
    photoUrl: "URL da foto",
    description: "Descrição",
    features: "Recursos",
    featuresHint: "(separados por vírgula)",
    featuresPlaceholder: "Bluetooth, Câmera de ré, Apple CarPlay",
    cancel: "Cancelar",
    saveChanges: "Salvar alterações",
  },
  onboarding: {
    stepAbout: "Sobre você",
    stepCar: "Seu carro",
    stepReview: "Revisar e anunciar",
    tierEconomy: "Econômicos e sedãs",
    tierSUV: "SUVs e picapes",
    tierPremium: "Premium (ex.: BMW X5)",
    perMo: "/mês",
    becomeHost: "Torne-se um Anfitrião",
    listYourCar: "Anuncie seu carro",
    intro: "Ganhe dinheiro compartilhando seu veículo na Belo Rentals. Qualquer marca, modelo, ano ou quilometragem — sem restrições de veículo.",
    fullName: "Nome completo",
    fullNamePlaceholder: "Maria Motorista",
    email: "E-mail",
    emailPlaceholder: "voce@email.com",
    mobilePhone: "Celular",
    phonePlaceholder: "(978) 555-0142",
    verified: "Verificado",
    sending: "Enviando…",
    resend: "Reenviar",
    sendCode: "Enviar código",
    enterCode: "Digite o código de 6 dígitos",
    verifying: "Verificando…",
    verify: "Verificar",
    devCode: "Modo dev: seu código é <0>{{code}}</0> (envio de SMS pendente de registro da operadora).",
    continue: "Continuar",
    vehicleVin: "Chassi (VIN) do veículo",
    vinPlaceholder: "VIN de 17 caracteres",
    decode: "Decodificar",
    qualifies: "Qualificado",
    tooOld: "Muito antigo",
    tooOldDetail: "Os veículos devem ter {{years}} anos ou menos ({{from}} ou mais recente).",
    trim: "Versão",
    trimPlaceholder: "ex.: EX-L, Sport",
    earnEstimate: "Carros como o seu ganham <0>US$ {{low}}–US$ {{high}}/mês</0>",
    odometer: "Hodômetro (milhas)",
    odometerPlaceholder: "ex.: 84000",
    odoError: "Deve ter menos de 130.000 milhas.",
    weeklyRate: "Tarifa semanal (US$)",
    vehicleType: "Tipo de veículo",
    photos: "Fotos",
    photosHint: "— pelo menos 5 fotos nítidas (exterior, interior, painel)",
    address: "Endereço do veículo / retirada",
    addressPlaceholder: "Comece a digitar seu endereço…",
    addressFallback: "Rua Principal, 123, Cidade, ST",
    back: "Voltar",
    vehicle: "Veículo",
    host: "Anfitrião",
    weeklyRateLabel: "Tarifa semanal",
    perWeek: "US$ {{rate}}/semana",
    certify: "Certifico que paguei todos os impostos sobre vendas aplicáveis a este veículo, que estou autorizado a anunciá-lo e que as informações fornecidas são precisas e completas.",
    submitting: "Enviando…",
    submitListing: "Enviar anúncio",
    underReviewTitle: "Seu carro está em análise",
    underReviewSub: "Nossa equipe geralmente analisa novos anúncios em 1–2 dias úteis, e então ele entra no ar na frota da Belo Rentals.",
    myAccount: "Minha Conta",
    browseFleet: "Explorar a Frota",
    enterPhoneFirst: "Digite seu número de telefone primeiro.",
    codeSent: "Código de verificação enviado para seu telefone.",
    enter6Digit: "Digite o código de 6 dígitos.",
    phoneVerifiedToast: "Telefone verificado!",
    enterVin: "Digite seu VIN.",
    certifyError: "Por favor, certifique a declaração de impostos para continuar.",
    contactHint: "Como podemos falar com você sobre seu anúncio e pagamentos?",
    docsTitle: "Seguro e registro",
    docsHint: "Envie uma foto nítida de cada documento.",
    insurance: "Seguro do veículo",
    registration: "Registro do veículo",
    uploadPhoto: "Enviar foto",
    change: "Alterar",
    uploaded: "Enviado",
    payoutTitle: "Dados de pagamento (transferência ACH)",
    payoutHint: "Para onde devemos enviar seus ganhos? Você fica com 100% do valor da viagem.",
    accountHolder: "Nome do titular da conta",
    routingNumber: "Número de roteamento (9 dígitos)",
    accountNumber: "Número da conta",
    accountType: "Tipo de conta",
    checking: "Conta corrente",
    savings: "Poupança",
    agreementStep: "Contrato do Marketplace",
    agreementTitle: "Contrato de Marketplace para Anfitriões",
    agreementIntro: "Leia e assine eletronicamente o Contrato de Marketplace para Anfitriões da Belo Rentals para anunciar seu carro.",
    agreementSignLabel: "Digite seu nome legal completo para assinar",
    agreementAgree: "Li e concordo com o Contrato de Marketplace para Anfitriões da Belo Rentals. Entendo que a Belo cobra uma taxa de serviço dos hóspedes e que eu fico com 100% dos meus ganhos de viagem.",
    agreementLoading: "Carregando contrato…",
    agreementError: "Por favor, leia e assine o contrato para continuar.",
    payoutError: "Insira dados de pagamento ACH válidos.",
    docsError: "Envie as fotos do seguro e do registro.",
    goToHostPortal: "Ir para o Portal do Anfitrião",
    keep85: "Você fica com 100% do valor da viagem.",
  },
};

const STORAGE_KEY = "belo_lang";
const stored = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
const initialLng = stored === "pt" || stored === "en" ? stored : "en";

i18n
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en }, pt: { translation: pt } },
    lng: initialLng,
    fallbackLng: "en",
    supportedLngs: ["en", "pt"],
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

export const setLanguage = (code) => {
  if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, code);
  return i18n.changeLanguage(code);
};

export default i18n;
